import React, { useEffect, useState, useCallback } from "react";

const romans = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV", "XVI"];

/**
 * Floating chapters TOC for the Story page.
 * Displays a vertical rail of Roman-numbered dots on the right edge (desktop only).
 * - Uses IntersectionObserver against `id="story-chapter-N"` markers set on each chapter.
 * - Clicking a dot smooth-scrolls to the chapter.
 * - Hidden on screens < xl (Tailwind: xl >= 1280px).
 *
 * `beats` is expected to be `dict.story.beats` — a list of chapter objects. Each item
 * uses its index → matching id `story-chapter-${index}`.
 */
export const StoryChaptersTOC = ({ beats }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!beats || beats.length === 0) return undefined;

    const els = beats
      .map((_, i) => document.getElementById(`story-chapter-${i}`))
      .filter(Boolean);
    if (els.length === 0) return undefined;

    // Track which chapters are visible; pick the one closest to viewport top.
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.dataset.chapterIndex);
          if (entry.isIntersecting) {
            visible.set(idx, entry.intersectionRatio);
          } else {
            visible.delete(idx);
          }
        });
        if (visible.size > 0) {
          const [best] = [...visible.entries()].sort((a, b) => b[1] - a[1]);
          setActiveIndex(best[0]);
        }
      },
      {
        // Trigger when a chapter's top enters the top ~40% of the viewport
        rootMargin: "-15% 0px -55% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [beats]);

  const jumpTo = useCallback((i) => {
    const el = document.getElementById(`story-chapter-${i}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (!beats || beats.length === 0) return null;

  return (
    <nav
      aria-label="Story chapters"
      className="hidden xl:flex fixed top-1/2 -translate-y-1/2 right-6 z-30 flex-col items-center gap-3 py-4 px-2 rounded-full bg-background/60 backdrop-blur border border-border/60 shadow-[0_18px_50px_rgba(31,31,31,0.06)]"
      data-testid="story-chapters-toc"
    >
      {beats.map((b, i) => {
        const isActive = i === activeIndex;
        const label = b.chapter || `Chapter ${romans[i] || i + 1}`;
        return (
          <button
            key={b.chapter || i}
            type="button"
            onClick={() => jumpTo(i)}
            aria-label={label}
            aria-current={isActive ? "true" : undefined}
            title={label}
            data-testid={`story-toc-item-${i}`}
            className="group relative flex items-center justify-center w-8 h-8 focus:outline-none"
          >
            <span
              className={
                "font-serif-display italic text-[13px] leading-none transition-colors " +
                (isActive ? "text-foreground" : "text-foreground/35 group-hover:text-foreground/70")
              }
            >
              {romans[i] || i + 1}
            </span>
            {/* Left accent bar for active */}
            <span
              aria-hidden="true"
              className={
                "absolute -left-1 top-1/2 -translate-y-1/2 h-3 w-[2px] rounded-full transition-all " +
                (isActive ? "bg-accent opacity-100" : "bg-transparent opacity-0")
              }
            />
            {/* Hover tooltip */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-foreground text-background px-2 py-1 text-[10px] tracking-[0.22em] uppercase opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default StoryChaptersTOC;
